import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AuthShell, Field, inputClass, btnClass, AuthLink } from "./AuthShell";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone_country_code: "+91", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [devLink, setDevLink] = useState("");

  const COUNTRY_CODES = ["+91", "+1", "+44", "+61", "+971", "+65", "+880", "+92", "+94", "+977"];

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6,15}$/.test(form.phone.trim())) { toast.error("Enter a valid mobile number"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, phone: form.phone.trim() });
      setDone(true);
      toast.success("Account created. Check your email to verify.");
      if (data.dev_verify_token) setDevLink(`/verify-email?token=${data.dev_verify_token}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  if (done) {
    return (
      <AuthShell title="Verify your email" subtitle="We sent a verification link to your inbox." testid="signup-done">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
            <MailCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <p className="text-sm text-slate-600">Click the link in your email to activate your account, then sign in.</p>
          {devLink && <a href={devLink} className="inline-block text-blue-600 font-medium hover:underline text-sm" data-testid="dev-verify-link">Dev: verify now →</a>}
          <div className="pt-2"><AuthLink to="/login">Go to sign in</AuthLink></div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Start managing your USDT transactions" testid="signup-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputClass} data-testid="signup-name-input" placeholder="Your name" />
        </Field>
        <Field label="Email">
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputClass} data-testid="signup-email-input" placeholder="you@example.com" />
        </Field>
        <Field label="Mobile number">
          <div className="flex gap-2 w-full">
            <select value={form.phone_country_code} onChange={(e) => setForm({ ...form, phone_country_code: e.target.value })}
              className={`${inputClass} !w-[96px] shrink-0 px-2`} data-testid="signup-country-code-select">
              {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="tel" inputMode="numeric" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })}
              className={`${inputClass} flex-1 min-w-0`} data-testid="signup-phone-input" placeholder="Mobile number" />
          </div>
        </Field>
        <Field label="Password">
          <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputClass} data-testid="signup-password-input" placeholder="At least 8 characters" />
        </Field>
        <button type="submit" disabled={loading} className={btnClass} data-testid="signup-submit-button">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>
        <div className="text-sm text-center text-slate-600">Already registered? <AuthLink to="/login">Sign in</AuthLink></div>
      </form>
    </AuthShell>
  );
}
